import banner from "../../../courses/module-1/img4.png";

const modulo4 = {
  courseId: "finanzas-personales",
  id: "modulo-4",
  title: "📘 MODULE 4: Responsible Credit Use",
  estimatedDuration: "2 hours",
  format: "Reading + analysis activity + practical assessment",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objectives",
      title: "🎯 Module objectives",
      content: [
        "Understand what credit is and how to use it responsibly.",
        "Learn how the credit bureau works and how it impacts your financial history.",
        "Learn to identify dangerous or abusive credit offers.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Main reading",
      title: "📖 Main reading: What is credit and how do you use it well?",
      content: [
        {
          type: "paragraph",
          text: "📌 What is credit?",
        },
        {
          type: "paragraph",
          text: "Credit is money that an institution, such as a bank or store, lends you with the commitment that you will pay it back within a defined period and with additional interest.",
        },
        "In simple words:",
        {
          type: "paragraph",
          text: "Credit is not extra money; it is borrowed money that will cost you more if you do not use it carefully.",
        },
      ],
    },
    {
      id: "util",
      navLabel: "Can it be useful?",
      title: "✅ When can it be useful?",
      content: [
        {
          type: "list",
          items: [
            "For large purchases you cannot pay for all at once, such as a laptop or refrigerator.",
            "For real emergencies when you do not have a savings fund.",
            "To build credit history, if you know how to use it responsibly.",
          ],
        },
      ],
    },
    {
      id: "problema",
      navLabel: "Can it be a problem?",
      title: "❌ When can it become a problem?",
      content: [
        {
          type: "list",
          items: [
            "When you use it to spend more than you earn.",
            "When you only pay the “minimum” each month.",
            "When you use it as an extension of your income, meaning you live off credit.",
            "When you do not know how much it will cost you in the end, including interest, fees, and penalties.",
          ],
        },
        {
          type: "paragraph",
          text: "Many young people end up trapped in debt early because of lack of information, not because of bad intentions or irresponsibility.",
        },
      ],
    },
    {
      id: "complementaria",
      navLabel: "Complementary reading",
      title: "📖 Complementary reading: What is the credit bureau?",
      content: [
        {
          type: "paragraph",
          text: "The Credit Bureau is an institution that keeps a record of how you use credit: whether you pay on time, whether you fall behind, whether you have active debts, and so on.",
        },
        {
          type: "list",
          items: [
            "If your history is positive: you get access to better credit with lower rates.",
            "If your history is negative: you may be denied loans or charged higher interest.",
            'If you have no history: it is like being "invisible" to the financial system.',
          ],
        },
        {
          type: "paragraph",
          text: "Having credit history is not bad; having bad credit history is the problem.",
        },
      ],
    },
    {
      id: "identificacion",
      navLabel: "Identify credit offers",
      title: "🛠 How to identify dangerous credit offers",
      content: [
        {
          type: "list",
          items: [
            "APR or total annual cost: the real percentage cost of credit, including interest and fees.",
            "Monthly and annual interest: the lower, the better.",
            "Hidden fees: opening fees, maintenance fees, late payment fees, etc.",
            "Contract terms: timelines, penalties, automatic charges.",
            "The source: avoid credit offers that sound too good or come from suspicious social media accounts.",
          ],
        },
        {
          type: "paragraph",
          text: "Pro tip: If you do not understand the terms, do not sign anything yet. Ask, research, or get help.",
        },
      ],
    },
    {
      id: "guia",
      navLabel: "Mini guide",
      title: "💬 Mini guide: good credit use",
      content: [
        {
          type: "list",
          items: [
            "Always pay the full amount, not only the minimum.",
            "Do not use more than 30% of your card limit.",
            "Activate payment alerts on your phone.",
            "Do not combine debt with more debt.",
          ],
        },
        {
          type: "link",
          href: "https://www.burodecredito.com.mx/",
          text: "You can check your credit bureau report for free once a year",
        },
      ],
    },
    {
      id: "extra",
      navLabel: "Extra",
      title: "📊 Extra: The Pareto Principle and credit use",
      content: [
        {
          type: "paragraph",
          text: "📌 What is the Pareto Principle? Also known as the 80/20 rule, the Pareto Principle says that a small number of causes usually generate most of the effects.",
        },
        {
          type: "paragraph",
          text: "🔗 How does this connect to credit use? In your financial life:",
        },
        {
          type: "list",
          items: [
            "20% of your credit decisions can generate 80% of your problems or benefits.",
            "One bad use, such as paying late or using your entire limit, can cause most of your stress or debt.",
            "But also, one smart action, such as always paying on time, can give you most of the benefits.",
          ],
        },
        {
          type: "list",
          items: [
            "Identify the 20% of habits that most affect your financial health.",
            "Change only one or two key things first.",
            "Get the biggest impact with a few moves.",
          ],
        },
        {
          type: "paragraph",
          text: "💬 “It is not about doing a lot, but about doing what truly matters.”",
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Practical activity",
      title: "📝 Assessment: Practical activity",
      content: [
        {
          type: "paragraph",
          text: "Activity: Evaluate and comment on a real credit card offer",
        },
        "What should you do?",
        {
          type: "paragraph",
          text: "1. Search for a real credit card on a bank or department store website.",
        },
        {
          type: "paragraph",
          text: "2. Copy the main information:",
        },
        {
          type: "list",
          items: [
            "Initial credit limit",
            "Average total annual cost",
            "Annual interest",
            "Fees",
            "Requirements to obtain it",
          ],
        },
        {
          type: "paragraph",
          text: "3. Analyze and answer:",
        },
        {
          type: "list",
          items: [
            "Is this card convenient for me as a student? Why?",
            "What risks do I see if I do not use it well?",
            "What safer alternatives could I consider?",
          ],
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliography",
      title: "📚 Recommended bibliography",
      content: [
        "Sofía Macías – “Pequeño Cerdo Capitalista: Finanzas para jóvenes con futuro”.",
        "CONDUSEF – “Credit card guide”.",
        "Banxico Financial Education – Free course for young people.",
        "Blog and YouTube channel: “Querido Dinero” (MX).",
      ],
    },
    {
      id: "frase",
      navLabel: "Quote of the day",
      title: "🧠 Quote of the day",
      content: [
        "“Credit is not bad. What is bad is using it without knowing what it really costs.”",
      ],
    },
  ],
};

export default modulo4;
